import { HttpClient } from '@angular/common/http';
import { computed, Inject, Signal, signal } from '@angular/core';
import { BaseEntity } from '@core/models';
import { PaginationService } from '@shared/components';

import { catchError, Observable, tap, throwError } from 'rxjs';

export class BaseService<T extends BaseEntity> extends PaginationService<T> {
  // SIGNALS
  protected items = signal<T[]>([]);
  protected selectedItemId = signal<number | null>(null);

  // PROPIEDADES COMPUTADAS
  protected selectedItem = computed(() => this.items().find((item) => item.id === this.selectedItemId()) || null);
  private totalRecords = computed(() => this.filteredItems().length);
  private paginatedItems = computed(() => {
    const filtered = this.filteredItems();
    const startIndex = (this.currentPage() - 1) * this.recordsPerPage();
    return filtered.slice(startIndex, startIndex + this.recordsPerPage());
  });
  private filteredItems = computed(() => {
    let filtered = [...this.items()];

    // Filtro por búsqueda
    const query = this.debouncedSearchQuery().toLowerCase();
    if (query && this.filterFunctionSearch) {
      filtered = filtered.filter((item) => this.filterFunctionSearch!(item, query));
    }

    // Aplicar filtros adicionales (si la subclase los define)
    if (this.applyAdditionalFilters) {
      filtered = this.applyAdditionalFilters(filtered);
    }

    // Ordenamiento
    if (this.sortField()) {
      filtered.sort((a, b) => {
        const field = this.sortField()!;
        const valueA = a[field];
        const valueB = b[field];

        return this.sortDirection() === 'asc' ? (valueA < valueB ? -1 : 1) : valueA > valueB ? -1 : 1;
      });
    }

    return filtered;
  });

  // METODOS ABSTRACTOS
  protected filterFunctionSearch?(item: T, query: string): boolean;
  protected applyAdditionalFilters?(items: T[]): T[];

  constructor(
    @Inject('API_URL') protected apiUrl: string,
    protected http: HttpClient,
  ) {
    super();
  }

  // GETTER AND SETTERS

  getTotalRecords(): Signal<number> {
    return this.totalRecords;
  }

  getItemsWithFilter(): Signal<T[]> {
    return this.paginatedItems;
  }

  getItems(): Signal<T[]> {
    return this.items;
  }

  getSelecteItem(): Signal<T | null> {
    return this.selectedItem;
  }

  setSelectedItem(id: number | null): void {
    this.selectedItemId.set(id);
  }

  // CRUD

  fetchById<O>(id: number): Observable<O> {
    return this.http.get<O>(`${this.apiUrl}/${id}`).pipe(
      // tap((entity) => this.entity.set(entity)),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  fetchItems(): Observable<T[]> {
    return this.http.get<T[]>(`${this.apiUrl}`).pipe(
      tap((items) => this.items.set(items)),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  addItem<I>(data: I | T) {
    return this.http.post<T>(`${this.apiUrl}`, data).pipe(
      tap((item) => {
        this.items.set([...this.items(), item]);
        this.selectedItemId.set(item.id);
      }),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  updateItem<I>(item: I | T): Observable<T> {
    if (!this.selectedItem()) return throwError(() => 'No hay item seleccionado');

    return this.http.put<T>(`${this.apiUrl}/${this.selectedItemId()}`, item).pipe(
      tap((updatedItem) => {
        this.items.set(this.items().map((u) => (u.id === updatedItem.id ? updatedItem : u)));
      }),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  inactiveItem(statusId: number): Observable<void> {
    const item = this.selectedItem();
    if (!item) return throwError(() => 'No hay item seleccionado');

    const updatedItem = { ...item, statusId: statusId };
    return this.http.delete<void>(`${this.apiUrl}/${item.id}`).pipe(
      tap(() => {
        this.items.set(this.items().map((u) => (u.id === item.id ? updatedItem : u)));
      }),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  toggleSort(field: keyof T) {
    let newDirection: 'asc' | 'desc' | null;

    if (this.sortField() === field) {
      newDirection = this.sortDirection() === 'asc' ? 'desc' : this.sortDirection() === 'desc' ? null : 'asc';
    } else {
      newDirection = 'asc';
    }

    this.setSort(field, newDirection ?? 'asc'); // Si es null, lo tratamos como 'asc'
  }
}
